import * as Sentry from "@sentry/react";
import { ApiError, HTTP_STATUS } from "@sidegigllc/api";
import type { QueryCache } from "@tanstack/react-query";

export function captureException(...args: Parameters<typeof Sentry.captureException>) {
  if (!processError(args[0]).shouldCapture) return;
  args[0] = toError(args[0]);
  Sentry.captureException(...args);
}

export const captureQueryError: QueryCache["config"]["onError"] = (error, query) => {
  const { shouldCapture, data } = processError(error);
  if (!shouldCapture) return;

  Sentry.withScope((scope) => {
    scope.setContext("query", { queryHash: query.queryHash, queryKey: query.queryKey, responseData: data });
    scope.setFingerprint([JSON.stringify(query.queryKey[0])]);
    Sentry.captureException(toError(error));
  });
};

// Wrap non-Error throwables so Sentry titles them; keep the payload on `cause`.
function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  return new Error(describeNonError(error), { cause: error });
}

function describeNonError(payload: unknown): string {
  return payload && typeof payload === "object"
    ? `Non-Error exception (keys: ${Object.keys(payload).join(", ") || "none"})`
    : `Non-Error exception (${String(payload)})`;
}

/** Benign browser/embedder noise and expected backend responses — dropped in beforeSend. */
export const ignoredMessages = [
  "Request aborted",
  "browser-tabs-lock-key",
  "Transition was aborted because of invalid state", // View Transitions API on inactive document
  "Failed to execute 'removeChild' on 'Node'", // browser extensions reparenting React DOM (facebook/react#11538)
  "WKWebView API client did not respond to this postMessage", // in-app browsers injecting scripts
  "signal timed out", // fetch AbortSignal.timeout
  "signal is aborted without reason",
  "Converting circular structure to JSON", // browser extensions patching appendChild to JSON.stringify DOM nodes
  "Request was throttled", // DRF 429 throttle
  "unauthorised", // 401 auth failure
  "Authentication credentials were not provided", // DRF 401
  "You do not have permission to perform this action", // DRF 403, e.g. captain deep-linking to an unassigned roster
  "Token has expired", // expired chat deep-link token; UI already renders <InvalidToken/>
];

/**
 * Match the original message and the primary parsed value (Sentry orders it last), not linked
 * cause/AggregateError children — a real error whose cause carries an ignored substring must survive.
 */
export function hasIgnoredMessage(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
  ignoredMessages: Array<string>,
): boolean {
  const originalMessage = hint.originalException instanceof Error ? hint.originalException.message : undefined;
  const primaryValue = event.exception?.values?.at(-1)?.value;
  const messages = [originalMessage, primaryValue];
  return messages.some((message) => !!message && ignoredMessages.some((ignored) => message.includes(ignored)));
}

// vendor frames we can't fix: Zoom blob: workers, Contentsquare /uxa
export const ignoredFrameUrls = ["blob:", "/uxa/"];

export function hasIgnoredFrame(event: Sentry.ErrorEvent, ignoredFrameUrls: Array<string>): boolean {
  // only the primary exception's throw-site frame — not linked causes or deeper first-party frames
  const filename = event.exception?.values?.at(-1)?.stacktrace?.frames?.at(-1)?.filename;
  return !!filename && ignoredFrameUrls.some((url) => filename.includes(url));
}

/** WebKit reports extension/translation DOM-reparenting unmounts as a bare NotFoundError that also
 * covers real ones (IndexedDB/Sendbird); gate the drop on the native removeChild frame, not the
 * message (Chromium's wording is in ignoredMessages). */
export function isDomReparentingError(event: Sentry.ErrorEvent): boolean {
  const primaryException = event.exception?.values?.at(-1);
  if (!primaryException?.value?.includes("The object can not be found here.")) return false;
  return !!primaryException.stacktrace?.frames?.some((frame) => frame.function?.includes("removeChild"));
}

/** Auto-instrumentation captures bypass toError(); backfill a title so non-Errors don't collapse to "<unknown>". */
export function normalizeSentryEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent {
  if (hint.originalException instanceof Error) return event;
  const description = describeNonError(hint.originalException);
  const values = event.exception?.values ?? [];
  if (values.length > 0) {
    for (const value of values) {
      if (!value.value) value.value = description;
    }
  } else if (!event.message) {
    event.message = description;
  }
  return event;
}

/** Auto-instrumentation captures 5xx ApiErrors directly, bypassing processError's drop; re-check status. */
export function isServerApiError(hint: Sentry.EventHint): boolean {
  return hint.originalException instanceof ApiError && (hint.originalException.httpStatus ?? 0) >= 500;
}

/** Keyless non-Error rejections (`Promise.reject({})`) carry no message, keys, or stack — nothing to act on. */
export function isEmptyObjectRejection(hint: Sentry.EventHint): boolean {
  const { originalException } = hint;
  return (
    typeof originalException === "object" &&
    originalException !== null &&
    !(originalException instanceof Error) &&
    Object.keys(originalException).length === 0
  );
}

const ignoredErrors = ["AbortError", "NetworkError", "Network Error"].map((error) => error.toLowerCase());
const includedStatuses = new Set<number>([HTTP_STATUS.BAD_REQUEST, HTTP_STATUS.TEAPOT]);

function processError(error: unknown): { shouldCapture: boolean; data?: ResponseData } {
  if (!(error instanceof Error)) return { shouldCapture: true };
  const errorMessage = error.message.toLowerCase();
  if (ignoredErrors.some((ignored) => errorMessage.includes(ignored))) {
    return { shouldCapture: false };
  }
  if (error.message.includes("Zod")) return { shouldCapture: true };

  const apiError = ApiError.fromThrown(error);
  // 5xx are backend failures, not FE bugs.
  if ((apiError.httpStatus ?? 0) >= 500) return { shouldCapture: false };

  const responseData = getResponseData(apiError);
  if (!responseData) {
    return { shouldCapture: apiError.httpStatus === undefined || includedStatuses.has(apiError.httpStatus) };
  }
  if (includedStatuses.has(responseData.status)) return { shouldCapture: true, data: responseData };

  return { shouldCapture: false };
}

type ResponseData = {
  message: string;
  response: unknown;
  method: string;
  status: number;
  url: string;
};
function getResponseData(apiError: ApiError): ResponseData | undefined {
  if (apiError.isUnknownError()) return;

  return {
    status: apiError.httpStatus ?? 42,
    method: apiError.httpMethod ?? "unknown",
    message: apiError.getMainErrorMessage(),
    response: apiError.rawData,
    url: apiError.url ?? "Unknown URL",
  };
}
